import type { ScreenId } from './contracts.ts'

export const BRIDGE_VERSION = 1 as const

type NativeMessage =
  | { type: 'configure'; payload: { platform: 'iOS' | 'Android'; apiBaseUrl: string; accessToken?: string | null; initialPath?: string; colorScheme?: 'dark' | 'light' | 'system' } }
  | { type: 'sessionChanged'; payload: { accessToken: string | null } }
  | { type: 'navigate'; payload: { path: string } }
  | { type: 'refresh'; payload: Record<string, never> }

interface Envelope<TType extends string = string, TPayload = unknown> {
  version: number
  source: 'bellhook.web' | 'bellhook.native'
  id: string
  type: TType
  payload: TPayload
}

type Handler = (message: NativeMessage) => void
let handler: Handler | null = null
let configured = false
let resolveConfiguration: (() => void) | null = null
const configuration = new Promise<void>((resolve) => { resolveConfiguration = resolve })

function messageId(): string {
  return `msg_${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`}`
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function nativeMessage(type: string, value: unknown): NativeMessage | null {
  const payload = record(value)
  if (!payload) return null
  switch (type) {
    case 'configure':
      if ((payload.platform !== 'iOS' && payload.platform !== 'Android') || typeof payload.apiBaseUrl !== 'string') return null
      if (payload.accessToken !== undefined && payload.accessToken !== null && typeof payload.accessToken !== 'string') return null
      if (payload.initialPath !== undefined && typeof payload.initialPath !== 'string') return null
      if (payload.colorScheme !== undefined && !['dark', 'light', 'system'].includes(String(payload.colorScheme))) return null
      return {
        type,
        payload: {
          platform: payload.platform,
          apiBaseUrl: payload.apiBaseUrl,
          ...(payload.accessToken !== undefined ? { accessToken: payload.accessToken as string | null } : {}),
          ...(typeof payload.initialPath === 'string' ? { initialPath: payload.initialPath } : {}),
          ...(payload.colorScheme !== undefined ? { colorScheme: payload.colorScheme as 'dark' | 'light' | 'system' } : {}),
        },
      }
    case 'sessionChanged':
      return payload.accessToken === null || typeof payload.accessToken === 'string'
        ? { type, payload: { accessToken: payload.accessToken } }
        : null
    case 'navigate':
      return typeof payload.path === 'string' ? { type, payload: { path: payload.path } } : null
    case 'refresh':
      return { type, payload: {} }
    default:
      return null
  }
}

function receive(value: unknown): void {
  try {
    const envelope = (typeof value === 'string' ? JSON.parse(value) : value) as Partial<Envelope>
    if (envelope?.version !== BRIDGE_VERSION || envelope.source !== 'bellhook.native' ||
        typeof envelope.id !== 'string' || envelope.id.length === 0 || typeof envelope.type !== 'string') return
    const message = nativeMessage(envelope.type, envelope.payload)
    if (!message) return
    handler?.(message)
    if (message.type === 'configure' && !configured) {
      configured = true
      resolveConfiguration?.()
    }
  } catch { /* Malformed native messages are intentionally ignored. */ }
}

export function installBridge(next: Handler): void {
  handler = next
  window.BellhookBridge = { version: BRIDGE_VERSION, receive }
}

export function isEmbedded(): boolean {
  return Boolean(window.webkit?.messageHandlers?.bellhook || window.BellhookNative || window.parent !== window)
}

export async function waitForNativeConfiguration(): Promise<void> {
  if (!isEmbedded() || configured) return
  await Promise.race([configuration, new Promise<void>((resolve) => window.setTimeout(resolve, 1500))])
}

function post<TType extends string, TPayload>(type: TType, payload: TPayload): void {
  const envelope: Envelope<TType, TPayload> = { version: BRIDGE_VERSION, source: 'bellhook.web', id: messageId(), type, payload }
  if (window.webkit?.messageHandlers?.bellhook) window.webkit.messageHandlers.bellhook.postMessage(envelope)
  else if (window.BellhookNative) window.BellhookNative.postMessage(JSON.stringify(envelope))
  else if (window.parent !== window) window.parent.postMessage(envelope, window.location.origin)
}

export const reportReady = (screen: ScreenId): void => post('ready', { bridgeVersion: BRIDGE_VERSION, screen })
export const reportNativeScreen = (screen: ScreenId): void => post('screen', { screen })
export const reportNativeError = (screen: ScreenId, code: string): void => post('error', { screen, code })
export const requestOpenExternal = (url: string): void => post('openExternal', { url })

declare global {
  interface Window {
    BellhookBridge: { version: number; receive(value: unknown): void }
    BellhookNative?: { postMessage(value: string): void }
    webkit?: { messageHandlers?: { bellhook?: { postMessage(value: unknown): void } } }
  }
}
